<script setup lang="ts">
import type { GuestbookMessage } from "@/models/message"
import { computed } from "vue"
import { Button, DropdownMenu, Icon, ReadMore } from "@/components/ui"
import { useTranslation } from "@/composables"
import { GuestbookMessageType } from "@/models/message"

const props = withDefaults(
  defineProps<{
    message?: GuestbookMessage
    isLoading?: boolean
    canDelete?: boolean
    canEdit?: boolean
  }>(),
  { message: undefined, isLoading: false, canDelete: false, canEdit: false },
)

const emit = defineEmits<{
  (e: "delete", messageId: number): void
  (e: "edit", message: GuestbookMessage): void
  (e: "flag", message: GuestbookMessage): void
}>()

const { t } = useTranslation()

function onDelete() {
  if (!props.message?.id)
    return

  emit("delete", props.message.id)
}

function onEdit() {
  if (!props.message?.id)
    return

  emit("edit", props.message)
}

function onFlag() {
  if (!props.message?.id)
    return

  emit("flag", props.message)
}

const messageMenuItems = computed(() => [
  {
    key: "flag",
    label: t("message_tile.flag"),
    icon: "Flag" as const,
    onClick: onFlag,
    dismissOnInteract: true,
  },
])
</script>

<template>
  <div v-if="isLoading">
    <div class="gpx-skeleton mb-2 h-24 w-full" />
    <div class="flex items-center gap-2">
      <div class="gpx-skeleton size-8" />
      <div class="gpx-skeleton h-8 w-64" />
    </div>
  </div>
  <div v-else-if="message" class="font-body">
    <div class="flex items-start justify-between font-normal md:mb-2">
      <div v-if="message.type === GuestbookMessageType.Text">
        <ReadMore :content="message.message" :lines="5" />
      </div>
      <div v-else-if="message.type === GuestbookMessageType.Video && message.videoUrl" class="w-full">
        <video class="max-h-[80vh] w-full md:max-h-[65vh]" controls :poster="message.snapshotUrl">
          <source :src="message.videoUrl" type="video/mp4">
        </video>
      </div>
    </div>
    <div class="flex items-center justify-between">
      <div>
        <div class="flex items-center gap-2 font-medium text-primary">
          <Icon icon="Pen" size="sm" class="shrink-0 md:h-6 md:w-6" />
          <span data-test="message-tile-from-name" class="line-clamp-1 overflow-hidden">{{ message.fromName }}</span>
        </div>
      </div>
      <div class="message-tile-icon-actions flex items-center gap-2">
        <Button v-if="canEdit" data-test="edit-message" variant="flat" :aria-label="t('message_tile.edit')" @click="onEdit">
          <Icon icon="Edit" size="sm" class="md:h-6 md:w-6" />
        </Button>
        <Button v-if="canDelete" data-test="delete-message" variant="flat" :aria-label="t('message_tile.delete')" @click="onDelete">
          <Icon icon="Trash" size="sm" class="md:h-6 md:w-6" />
        </Button>
        <DropdownMenu :items="messageMenuItems" side="bottom" align="end">
          <template #activator="{ isOpen }">
            <Button
              data-test="message-actions-menu"
              variant="flat"
              type="button"
              :aria-label="t('message_tile.more_actions')"
              :aria-expanded="isOpen"
            >
              <Icon icon="MenuEllipsisHorizontal" size="sm" class="md:h-6 md:w-6" />
            </Button>
          </template>
        </DropdownMenu>
      </div>
    </div>
  </div>
</template>

<style scoped>
.message-tile-icon-actions :deep(.gpx-button) {
  padding: 0;
}
</style>
